import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { generatePdfFromElement } from '../../utils/pdfUtils';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement, RadialLinearScale } from 'chart.js';
import { Pie, Bar, Line, Radar } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  PointElement, 
  LineElement,
  RadialLinearScale
);

// Material-UI components
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Paper,
  Chip,
  Avatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  CircularProgress,
  Slider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
  IconButton,
  Tabs,
  Tab,
  useMediaQuery,
  Stepper,
  Step,
  StepLabel,
  Fade,
  Badge,
  LinearProgress,
  useTheme
} from '@mui/material';

// Material-UI icons
import {
  ArrowForward,
  FileDownload,
  CheckCircle,
  Cancel,
  Star,
  StarHalf,
  StarOutline,
  VerifiedUser,
  PieChart,
  ShowChart,
  Insights,
  Assessment,
  TrendingUp,
  People,
  Psychology,
  Payment,
  Dashboard,
  BusinessCenter,
  Apartment,
  CreditCard,
  Group,
  EventNote,
  Notifications,
  EmojiEvents,
  Redeem,
  Favorite,
  FavoriteBorder,
  HelpOutline,
  Message,
  ForumOutlined,
  FeedbackOutlined,
  AssignmentOutlined,
  RequestPage,
  CheckCircleOutline,
  Print,
  Close,
  Timeline,
  MonetizationOn,
  Speed,
  KeyboardArrowUp,
  KeyboardArrowDown,
  TouchApp,
  BarChart,
  DonutLarge
} from '@mui/icons-material';

// Data for charts
const memberEngagementData = {
  labels: ['With Our Platform', 'Without Our Platform'],
  datasets: [
    {
      data: [83, 47],
      backgroundColor: ['rgba(76, 175, 80, 0.8)', 'rgba(158, 158, 158, 0.5)'],
      borderColor: ['#4caf50', '#9e9e9e'],
      borderWidth: 1,
    },
  ],
};

const revenueComparisonData = {
  labels: ['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'],
  datasets: [
    {
      label: 'With Our Platform',
      data: [120000, 156000, 195000, 234000, 273000],
      backgroundColor: 'rgba(25, 118, 210, 0.6)',
      borderColor: '#1976d2',
      borderWidth: 2,
    },
    {
      label: 'Without Our Platform',
      data: [100000, 105000, 110000, 115000, 121000],
      backgroundColor: 'rgba(158, 158, 158, 0.4)',
      borderColor: '#9e9e9e',
      borderWidth: 2,
    },
  ],
};

const memberRetentionData = {
  labels: ['Q1', 'Q2', 'Q3', 'Q4'],
  datasets: [
    {
      label: 'With Our Platform',
      data: [92, 93, 95, 96],
      backgroundColor: 'rgba(103, 58, 183, 0.3)',
      borderColor: '#673ab7',
      borderWidth: 2,
      tension: 0.4,
    },
    {
      label: 'Without Our Platform',
      data: [75, 73, 72, 70],
      backgroundColor: 'rgba(158, 158, 158, 0.3)',
      borderColor: '#9e9e9e',
      borderWidth: 2,
      tension: 0.4,
    },
  ],
};

const competitorRatingData = {
  labels: ['Member Management', 'Payment Processing', 'Analytics', 'Member Portal', 'Engagement', 'Integration'],
  datasets: [
    {
      label: 'Our Platform',
      data: [5, 5, 5, 5, 5, 5],
      backgroundColor: 'rgba(76, 175, 80, 0.6)',
      borderColor: '#4caf50',
      borderWidth: 2,
    },
    {
      label: 'Competitor A',
      data: [4, 4, 3, 4, 3, 3],
      backgroundColor: 'rgba(25, 118, 210, 0.6)',
      borderColor: '#1976d2',
      borderWidth: 2,
    },
    {
      label: 'Competitor B',
      data: [3, 3, 2, 3, 2, 2],
      backgroundColor: 'rgba(255, 152, 0, 0.6)',
      borderColor: '#ff9800',
      borderWidth: 2,
    },
  ],
};

// Admin analytics data for pie chart
const adminTimeAllocationData = {
  labels: ['Member Support', 'Financial Admin', 'Content Management', 'Technical Issues', 'Strategy & Planning'],
  datasets: [
    {
      data: [35, 25, 15, 15, 10],
      backgroundColor: [
        'rgba(33, 150, 243, 0.8)',
        'rgba(76, 175, 80, 0.8)',
        'rgba(156, 39, 176, 0.8)',
        'rgba(255, 152, 0, 0.8)',
        'rgba(244, 67, 54, 0.8)'
      ],
      borderColor: '#ffffff',
      borderWidth: 2,
    },
  ],
};

// Member Rewards Suite features
const rewardsFeatures = [
  {
    id: 1,
    name: 'Points-Based Rewards',
    description: 'MembaCoins system to reward members for engagement and activity',
    icon: <EmojiEvents sx={{ fontSize: 40, color: '#ff9800' }} />,
    gradient: 'linear-gradient(135deg, #fff9c4 0%, #ffeb3b 100%)'
  },
  {
    id: 2,
    name: 'Achievement Badges',
    description: 'Celebrate milestones with badges for achievements and engagement',
    icon: <VerifiedUser sx={{ fontSize: 40, color: '#4caf50' }} />,
    gradient: 'linear-gradient(135deg, #c8e6c9 0%, #81c784 100%)'
  },
  {
    id: 3,
    name: 'Member Loyalty Program',
    description: 'Retain members with tiered loyalty programs offering benefits by activity',
    icon: <Favorite sx={{ fontSize: 40, color: '#e91e63' }} />,
    gradient: 'linear-gradient(135deg, #f8bbd0 0%, #f48fb1 100%)'
  },
  {
    id: 4,
    name: 'Gift and Incentive Management',
    description: 'Distribute digital or physical incentives with redemption tracking',
    icon: <Redeem sx={{ fontSize: 40, color: '#9c27b0' }} />,
    gradient: 'linear-gradient(135deg, #e1bee7 0%, #ce93d8 100%)'
  }
];

// Member Relationship Suite features
const relationshipFeatures = [
  {
    id: 1,
    name: 'Member Journey Mapping',
    description: 'Design and automate personalized touchpoints to improve engagement',
    icon: <ShowChart sx={{ fontSize: 40, color: '#2196f3' }} />,
    gradient: 'linear-gradient(135deg, #bbdefb 0%, #90caf9 100%)'
  },
  {
    id: 2,
    name: 'Communication Hub',
    description: 'Centralize all member communications with automated emails',
    icon: <Message sx={{ fontSize: 40, color: '#00bcd4' }} />,
    gradient: 'linear-gradient(135deg, #b2ebf2 0%, #80deea 100%)'
  },
  {
    id: 3,
    name: 'Feedback Collection',
    description: 'Gather valuable insights through customized feedback with analytics',
    icon: <FeedbackOutlined sx={{ fontSize: 40, color: '#ff5722' }} />,
    gradient: 'linear-gradient(135deg, #ffccbc 0%, #ffab91 100%)'
  },
  {
    id: 4,
    name: 'Community Forums',
    description: 'Foster member-to-member connections with knowledge sharing',
    icon: <ForumOutlined sx={{ fontSize: 40, color: '#795548' }} />,
    gradient: 'linear-gradient(135deg, #d7ccc8 0%, #bcaaa4 100%)'
  }
];

// Member Request Suite features
const requestFeatures = [
  {
    id: 1,
    name: 'Request Workflow Builder',
    description: 'Create custom approval workflows for requests with logic and rules',
    icon: <AssignmentOutlined sx={{ fontSize: 40, color: '#3f51b5' }} />,
    gradient: 'linear-gradient(135deg, #c5cae9 0%, #9fa8da 100%)'
  },
  {
    id: 2,
    name: 'Self-Service Portal',
    description: 'Give members a portal to submit, track and manage requests in real-time',
    icon: <Dashboard sx={{ fontSize: 40, color: '#673ab7' }} />,
    gradient: 'linear-gradient(135deg, #d1c4e9 0%, #b39ddb 100%)'
  },
  {
    id: 3,
    name: 'Document Management',
    description: 'Secure document uploads and approvals for all members materials',
    icon: <RequestPage sx={{ fontSize: 40, color: '#607d8b' }} />,
    gradient: 'linear-gradient(135deg, #cfd8dc 0%, #b0bec5 100%)'
  },
  {
    id: 4,
    name: 'Request Analytics',
    description: 'Track revenue with dashboards,forecasts and detailed breakdowns',

    icon: <Assessment sx={{ fontSize: 40, color: '#009688' }} />,
    gradient: 'linear-gradient(135deg, #b2dfdb 0%, #80cbc4 100%)'
  }
];

// Competitive comparison data
const competitorComparison = [
  {
    feature: "Member Management",
    us: { rating: 5, note: "Comprehensive member profiles with custom fields and segmentation" },
    competitor1: { rating: 4, note: "Basic member management with limited customization" },
    competitor2: { rating: 3, note: "Limited profile options and manual segmentation" }
  },
  {
    feature: "Payment Processing",
    us: { rating: 5, note: "Multiple payment methods, recurring billing, and automated reconciliation" },
    competitor1: { rating: 4, note: "Limited payment options with manual reconciliation" },
    competitor2: { rating: 3, note: "Basic payment processing with no automation" }
  },
  {
    feature: "Analytics & Reporting",
    us: { rating: 5, note: "Real-time dashboards, custom reports, and predictive analytics" },
    competitor1: { rating: 3, note: "Basic reporting with limited customization" },
    competitor2: { rating: 2, note: "Minimal reporting capabilities" }
  },
  {
    feature: "Member Portal",
    us: { rating: 5, note: "Fully branded member experience with mobile optimization" },
    competitor1: { rating: 4, note: "Limited branding options with basic mobile support" },
    competitor2: { rating: 3, note: "Generic portal with minimal customization" }
  },
  {
    feature: "Engagement Tools",
    us: { rating: 5, note: "Comprehensive engagement features with gamification and rewards" },
    competitor1: { rating: 3, note: "Basic engagement tools with no gamification" },
    competitor2: { rating: 2, note: "Limited engagement features" }
  },
  {
    feature: "Integration Capabilities",
    us: { rating: 5, note: "Open API, webhooks, and native integrations with popular platforms" },
    competitor1: { rating: 3, note: "Limited API access with few native integrations" },
    competitor2: { rating: 2, note: "Minimal integration options" }
  }
];

// Render star ratings
const StarRating = ({ rating }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    if (i <= rating) {
      stars.push(<Star key={i} sx={{ color: '#FFC107' }} />);
    } else if (i - 0.5 === rating) {
      stars.push(<StarHalf key={i} sx={{ color: '#FFC107' }} />);
    } else {
      stars.push(<StarOutline key={i} sx={{ color: '#FFC107' }} />);
    }
  }
  return <Box sx={{ display: 'flex' }}>{stars}</Box>;
};

// Calculate ROI values
const calculateROI = (members, fees, collectionRate, adminHours, adminHourlyRate) => {
  // Current revenue
  const currentRevenue = members * fees * (collectionRate / 100);
  
  // Projected revenue with improved collection rate (up to 35% improvement)
  const projectedCollectionRate = Math.min(100, collectionRate + (35 * (1 - collectionRate / 100)));
  const projectedRevenue = members * fees * (projectedCollectionRate / 100);
  const revenueIncrease = projectedRevenue - currentRevenue;
  
  // Admin time savings (up to 70% reduction)
  const currentAdminCost = adminHours * 52 * adminHourlyRate; // Annual admin cost
  const projectedAdminCost = currentAdminCost * 0.3; // 70% reduction
  const adminSavings = currentAdminCost - projectedAdminCost;
  
  // Member retention improvement (up to 25% improvement)
  const retentionValue = members * fees * 0.25 * 0.2; // Assuming 20% churn reduction for 25% of members
  
  // Total ROI
  const totalAnnualSavings = revenueIncrease + adminSavings + retentionValue;
  const fiveYearSavings = totalAnnualSavings * 5;
  const roi = (totalAnnualSavings / (fees * 12)) * 100; // ROI percentage based on platform cost
  
  return {
    revenueIncrease,
    adminSavings,
    retentionValue,
    totalAnnualSavings,
    fiveYearSavings,
    roi
  };
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Column layout components
const TwoColumnLayout = ({ children, sx }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Split children into two groups
  const childrenArray = React.Children.toArray(children);
  const leftColumn = childrenArray.slice(0, Math.ceil(childrenArray.length / 2));
  const rightColumn = childrenArray.slice(Math.ceil(childrenArray.length / 2));
  
  return (
    <Box sx={{ width: '100%', ...sx }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Box sx={{ height: '100%' }}>
            {leftColumn}
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ height: '100%' }}>
            {rightColumn}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const FullWidthLayout = ({ children, sx }) => {
  return (
    <Box sx={{ width: '100%', ...sx }}>
      <Grid container>
        <Grid item xs={12}>
          <Box sx={{ height: '100%' }}>
            {children}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const MarketingMaterialsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Tab state for section navigation
  const [activeTab, setActiveTab] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  // ROI Calculator state
  const [roiValues, setRoiValues] = useState({
    members: 500,
    fees: 120,
    collectionRate: 75,
    adminHours: 20,
    adminHourlyRate: 25
  });
  
  const [roiResults, setRoiResults] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // DOM refs for PDF generation and scrolling
  const pageRef = useRef(null);
  const sectionsRef = useRef([]);
  
  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };
  
  // Handle scroll events to show/hide scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 500) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Calculate ROI on initial load and when values change
  useEffect(() => {
    const results = calculateROI(
      roiValues.members,
      roiValues.fees,
      roiValues.collectionRate,
      roiValues.adminHours,
      roiValues.adminHourlyRate
    );
    setRoiResults(results);
  }, [roiValues]);
  
  // Scroll to section on tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (sectionsRef.current[newValue]) {
      sectionsRef.current[newValue].scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  
  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  // Handle ROI input changes
  const handleRoiChange = (field) => (event, newValue) => {
    setRoiValues({
      ...roiValues,
      [field]: event.target ? Number(event.target.value) : newValue
    });
  };
  
  // Handle PDF generation
  const handleDownloadPDF = async () => {
    try {
      setIsPrinting(true);
      
      await generatePdfFromElement(
        pageRef.current,
        'Membership_Platform_Marketing_Materials',
        {
          onLoadingStart: () => setIsPrinting(true),
          onLoadingComplete: () => {
            setIsPrinting(false);
            setSnackbar({
              open: true,
              message: 'Marketing materials PDF generated successfully!',
              severity: 'success'
            });
          },
          onError: (error) => {
            console.error('PDF generation error:', error);
            setIsPrinting(false);
            setSnackbar({
              open: true,
              message: 'Failed to generate PDF: ' + error.message,
              severity: 'error'
            });
          }
        }
      );
    } catch (error) {
      console.error('Error generating PDF:', error);
      setIsPrinting(false);
      setSnackbar({
        open: true,
        message: 'Failed to generate PDF: ' + error.message,
        severity: 'error'
      });
    }
  };
  
  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Section titles for navigation
  const sections = [
    { title: "Overview", icon: <Dashboard /> },
    { title: "Member Rewards", icon: <EmojiEvents /> },
    { title: "Member Relationships", icon: <People /> },
    { title: "Request Management", icon: <AssignmentOutlined /> },
    { title: "Admin Features", icon: <BusinessCenter /> },
    { title: "ROI Calculator", icon: <MonetizationOn /> },
    { title: "Competitive Edge", icon: <TrendingUp /> }
  ];

  return (
    <>
      {/* Header with sticky navigation */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          boxShadow: 3,
          background: 'linear-gradient(135deg, #0a1929 0%, #1a2942 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ py: 2 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                textAlign: 'center',
                mb: 2,
                background: 'linear-gradient(90deg, #ffffff 0%, #bbdefb 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Membership Platform Marketing Materials
            </Typography>
            
            <Paper
              elevation={0}
              sx={{
                borderRadius: 8,
                overflow: 'hidden',
                bgcolor: 'rgba(255,255,255,0.08)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant={isMobile ? "scrollable" : "fullWidth"}
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  '& .MuiTab-root': {
                    color: 'rgba(255,255,255,0.7)',
                    fontWeight: 'medium',
                    py: 1.5,
                    mx: 0.5,
                    transition: 'all 0.2s',
                    minHeight: 'unset',
                    '&.Mui-selected': {
                      color: 'white',
                      fontWeight: 'bold',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                    background: 'linear-gradient(90deg, #1976d2, #9c27b0)',
                  },
                }}
              >
                {sections.map((section, index) => (
                  <Tab
                    key={index}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {section.icon}
                        <Box sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}>{section.title}</Box>
                      </Box>
                    }
                  />
                ))}
              </Tabs>
            </Paper>
          </Box>
        </Container>
      </Box>
      
      {/* Download PDF Button */}
      <Box 
        sx={{ 
          position: 'fixed', 
          bottom: { xs: 16, md: 24 }, 
          right: { xs: 16, md: 24 },
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        {showScrollTop && (
          <Fade in={showScrollTop}>
            <IconButton
              onClick={scrollToTop}
              sx={{
                bgcolor: 'white',
                boxShadow: 3,
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <KeyboardArrowUp />
            </IconButton>
          </Fade>
        )}
        
        <Button
          variant="contained"
          startIcon={<FileDownload />}
          onClick={handleDownloadPDF}
          disabled={isPrinting}
          sx={{
            py: 1.5,
            px: 2,
            borderRadius: 2,
            background: 'linear-gradient(90deg, #1976d2, #9c27b0)',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '&:hover': {
              background: 'linear-gradient(90deg, #1976d2, #7b1fa2)',
              boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
            },
          }}
        >
          {isPrinting ? 'Generating PDF...' : 'Download PDF'}
          {isPrinting && <CircularProgress size={24} sx={{ ml: 1, color: 'white' }} />}
        </Button>
      </Box>
      
      <div ref={pageRef}>
        {/* Hero Section with Overview */}
        <Box 
          ref={(el) => (sectionsRef.current[0] = el)}
          sx={{ 
            py: { xs: 6, md: 10 }, 
            background: 'linear-gradient(135deg, #0a1929 0%, #1a2942 100%)',
            color: 'white'
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ pr: { md: 4 } }}>
                  <Typography 
                    variant="h2" 
                    component="h1" 
                    fontWeight="800"
                    sx={{
                      mb: 2,
                      background: 'linear-gradient(90deg, #ffffff 0%, #bbdefb 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      lineHeight: 1.2
                    }}
                  >
                    Enterprise Membership Management Platform
                  </Typography>
                  <Typography variant="h5" sx={{ mb: 3, opacity: 0.9 }}>
                    A comprehensive solution for organizations to manage members, payments, and engagement
                  </Typography>
                  
                  <Box component="ul" sx={{ pl: 2, mb: 4 }}>
                    {['Member Rewards & Engagement', 'Relationship Management', 'Request Handling', 'Administrative Tools'].map((item, i) => (
                      <Box
                        component="li"
                        key={i}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 1.5,
                          opacity: 0.9
                        }}
                      >
                        <CheckCircle sx={{ mr: 1, color: '#4caf50' }} />
                        {item}
                      </Box>
                    ))}
                  </Box>
                  
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForward />}
                    component={Link}
                    to="/register-tenant"
                    sx={{
                      px: 4,
                      py: 1.5,
                      bgcolor: '#4caf50',
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: '#43a047',
                      },
                    }}
                  >
                    Get Started Free
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      ml: 2,
                      px: 4,
                      py: 1.5,
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.5)',
                      borderRadius: 2,
                      '&:hover': {
                        borderColor: 'white',
                        bgcolor: 'rgba(255,255,255,0.08)',
                      },
                    }}
                  >
                    Contact Sales
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    position: 'relative',
                    mt: { xs: 4, md: 0 }
                  }}
                >
                  <Paper
                    elevation={4}
                    sx={{
                      borderRadius: 4,
                      overflow: 'hidden',
                      position: 'relative',
                      width: '100%',
                      maxWidth: 1200,
                      bgcolor: 'rgba(255,255,255,0.09)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.12)'
                    }}
                  >
                    <Box sx={{ p: 3 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: 'white' }}>
                        Platform Performance Metrics
                      </Typography>
                      
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: 'rgba(255,255,255,0.07)',
                              height: '100%'
                            }}
                          >
                            <Typography variant="subtitle2" gutterBottom sx={{ opacity: 0.9 }}>
                              Member Engagement
                            </Typography>
                            <Box sx={{ height: 200, position: 'relative' }}>
                              <Pie 
                                data={memberEngagementData} 
                                options={{
                                  ...chartOptions,
                                  plugins: {
                                    ...chartOptions.plugins,
                                    legend: {
                                      ...chartOptions.plugins.legend,
                                      labels: { color: 'white' }
                                    }
                                  }
                                }}
                              />
                            </Box>
                            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', opacity: 0.8 }}>
                              83% engagement rate with our platform
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: 'rgba(255,255,255,0.07)',
                              height: '100%'
                            }}
                          >
                            <Typography variant="subtitle2" gutterBottom sx={{ opacity: 0.9 }}>
                              Average Admin Time Saved
                            </Typography>
                            <Box
                              sx={{
                                height: 200,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center'
                              }}
                            >
                              <Box sx={{ position: 'relative', width: 140, height: 200 }}>
                                <CircularProgress
                                  variant="determinate"
                                  value={100}
                                  size={140}
                                  thickness={4}
                                  sx={{ color: 'rgba(255,255,255,0.1)', position: 'absolute' }}
                                />
                                <CircularProgress
                                  variant="determinate"
                                  value={70}
                                  size={140}
                                  thickness={4}
                                  sx={{ color: '#4caf50', position: 'absolute' }}
                                />
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    bottom: 0,
                                    right: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Typography
                                    variant="h4"
                                    component="div"
                                    fontWeight="bold"
                                    sx={{ color: 'white' }}
                                  >
                                    70%
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', opacity: 0.8 }}>
                              Administrative time reduction
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: 'rgba(255,255,255,0.07)'
                            }}
                          >
                            <Typography variant="subtitle2" gutterBottom sx={{ opacity: 0.9 }}>
                              Member Retention Rates
                            </Typography>
                            <Box sx={{ height: 200 }}>
                              <Line 
                                data={memberRetentionData} 
                                options={{
                                  ...chartOptions,
                                  scales: {
                                    y: {
                                      min: 60,
                                      max: 100,
                                      grid: {
                                        color: 'rgba(255,255,255,0.1)'
                                      },
                                      ticks: { color: 'rgba(255,255,255,0.7)' }
                                    },
                                    x: {
                                      grid: {
                                        color: 'rgba(255,255,255,0.1)'
                                      },
                                      ticks: { color: 'rgba(255,255,255,0.7)' }
                                    }
                                  },
                                  plugins: {
                                    ...chartOptions.plugins,
                                    legend: {
                                      ...chartOptions.plugins.legend,
                                      labels: { color: 'white' }
                                    }
                                  }
                                }}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Box>
              </Grid>
            </Grid>
            
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              sx={{ mt: 8 }}
            >
              <Paper
                elevation={2}
                sx={{
                  borderRadius: 4,
                  p: 3,
                  background: 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.08)'
                }}
              >
                <Stepper
                  alternativeLabel
                  sx={{
                    '& .MuiStepLabel-label': {
                      color: 'rgba(255,255,255,0.7)',
                      mt: 1,
                      '&.Mui-completed': { color: 'white' },
                      '&.Mui-active': { color: 'white', fontWeight: 'bold' }
                    },
                    '& .MuiStepIcon-root': {
                      color: 'rgba(255,255,255,0.3)',
                      '&.Mui-completed': { color: '#4caf50' },
                      '&.Mui-active': { color: '#2196f3' }
                    }
                  }}
                >
                  <Step completed>
                    <StepLabel>Register</StepLabel>
                  </Step>
                  <Step completed>
                    <StepLabel>Configure</StepLabel>
                  </Step>
                  <Step completed>
                    <StepLabel>Invite Members</StepLabel>
                  </Step>
                  <Step active>
                    <StepLabel>Engage & Grow</StepLabel>
                  </Step>
                </Stepper>
              </Paper>
            </Box>
          </Container>
        </Box>
        
        {/* Member Rewards Suite Section */}
        <Box 
          ref={(el) => (sectionsRef.current[1] = el)}
          sx={{ 
            py: 10, 
            backgroundColor: 'white'
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ mb: 6, textAlign: 'center' }}>
              <Typography
                variant="subtitle1"
                color="primary"
                fontWeight="bold"
                gutterBottom
                sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
              >
                Membership Suite #1
              </Typography>
              <Typography
                variant="h3"
                component="h2"
                fontWeight="bold"
                gutterBottom
                sx={{
                  background: 'linear-gradient(90deg, #ff9800 30%, #f57c00 70%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Member Rewards Suite
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                Drive member engagement and retention with a comprehensive rewards ecosystem that recognizes and incentivizes valuable behaviors
              </Typography>
            </Box>
            
            <Grid container spacing={4}>
              {rewardsFeatures.map((feature) => (
                <Grid item xs={12} md={6} key={feature.id}>
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                  >
                    <Paper
                      elevation={2}
                      sx={{
                        borderRadius: 4,
                        overflow: 'hidden',
                        height: '100%',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                        }
                      }}
                    >
                      <Box sx={{ p: 4 }}>
                        <Box
                          sx={{
                            display: 'inline-block',
                            borderRadius: '16px',
                            p: 1.5,
                            mb: 2,
                            background: feature.gradient
                          }}
                        >
                          {feature.icon}
                        </Box>
                        <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                          {feature.name}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                          {feature.description}
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>
                </Grid>
              ))}
            </Grid>
            
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              sx={{ mt: 8 }}
            >
              <Paper
                elevation={2}
                sx={{ 
                  borderRadius: 4, 
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)'
                }}
              >
                <Box sx={{ p: 4 }}>
                  <Typography variant="h5" fontWeight="bold" gutterBottom color="#e65100">
                    Rewards Suite Impact
                  </Typography>
                  
                  <Grid container spacing={4} alignItems="center">
                    <Grid item xs={12} md={7}>
                      <Typography variant="body1" paragraph>
                        Organizations using our Member Rewards Suite report significant improvements in key metrics:
                      </Typography>
                      
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 2,
                          mb: 3
                        }}
                      >
                        <Chip
                          icon={<TrendingUp sx={{ color: '#4caf50 !important' }} />}
                          label="47% Higher Engagement"
                          sx={{
                            bgcolor: 'white',
                            fontWeight: 'medium',
                            border: '1px solid #e0e0e0'
                          }}
                        />
                        <Chip
                          icon={<People sx={{ color: '#2196f3 !important' }} />}
                          label="32% Lower Churn Rate"
                          sx={{
                            bgcolor: 'white',
                            fontWeight: 'medium',
                            border: '1px solid #e0e0e0'
                          }}
                        />
                        <Chip
                          icon={<MonetizationOn sx={{ color: '#f57c00 !important' }} />}
                          label="28% Higher Revenue Per Member"
                          sx={{
                            bgcolor: 'white',
                            fontWeight: 'medium',
                            border: '1px solid #e0e0e0'
                          }}
                        />
                      </Box>
                      
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.7)',
                          border: '1px solid rgba(0,0,0,0.05)'
                        }}
                      >
                        <Typography variant="body2" fontStyle="italic" color="text.secondary">
                          "The rewards program transformed our membership experience. We've seen unprecedented levels of engagement and our renewal rates are at an all-time high."
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: '#ff9800',
                              fontSize: '0.875rem',
                              fontWeight: 'bold'
                            }}
                          >
                            JD
                          </Avatar>
                          <Box sx={{ ml: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              Jane Detty
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Membership Director, Abacus Association
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Box sx={{ height: 300 }}>
                        <Bar
                          data={{
                            labels: ['Before', 'After'],
                            datasets: [
                              {
                                label: 'Member Engagement',
                                data: [47, 94],
                                backgroundColor: '#4caf50',
                              },
                              {
                                label: 'Member Retention',
                                data: [68, 90],
                                backgroundColor: '#2196f3',
                              },
                              {
                                label: 'Revenue Per Member',
                                data: [100, 128],
                                backgroundColor: '#ff9800',
                              }
                            ]
                          }}
                          options={{
                            ...chartOptions,
                            scales: {
                              y: {
                                beginAtZero: true,
                                max: 140,
                                title: {
                                  display: true,
                                  text: 'Percentage (%)'
                                }
                              }
                            },
                            plugins: {
                              legend: {
                                position: 'bottom'
                              },
                              title: {
                                display: true,
                                text: 'Impact of Rewards Program'
                              }
                            }
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Box>
          </Container>
        </Box>
        
        {/* Member Relationship Suite Section */}
        <Box 
          ref={(el) => (sectionsRef.current[2] = el)}
          sx={{ 
            py: 10, 
            backgroundColor: '#f8f9fa'
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ mb: 6, textAlign: 'center' }}>
              <Typography
                variant="subtitle1"
                color="primary"
                fontWeight="bold"
                gutterBottom
                sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
              >
                Membership Suite #2
              </Typography>
              <Typography
                variant="h3"
                component="h2"
                fontWeight="bold"
                gutterBottom
                sx={{
                  background: 'linear-gradient(90deg, #2196f3 30%, #03a9f4 70%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Member Relationship Suite
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                Build stronger connections with your members through personalized communications, feedback collection, and community building tools
              </Typography>
            </Box>
            
            <Grid container spacing={4}>
              {relationshipFeatures.map((feature) => (
                <Grid item xs={12} md={6} key={feature.id}>
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                  >
                    <Paper
                      elevation={2}
                      sx={{
                        borderRadius: 4,
                        overflow: 'hidden',
                        height: '100%',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                        }
                      }}
                    >
                      <Box sx={{ p: 4 }}>
                        <Box
                          sx={{
                            display: 'inline-block',
                            borderRadius: '16px',
                            p: 1.5,
                            mb: 2,
                            background: feature.gradient
                          }}
                        >
                          {feature.icon}
                        </Box>
                        <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                          {feature.name}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                          {feature.description}
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>
                </Grid>
              ))}
            </Grid>
            
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              sx={{ mt: 6 }}
            >
              <Paper 
                elevation={3}
                sx={{ 
                  borderRadius: 4, 
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                }}
              >
                <Box sx={{ p: 4 }}>
                  <Grid container spacing={4} alignItems="center">
                    <Grid item xs={12} md={7}>
                      <Typography variant="h4" fontWeight="bold" gutterBottom color="#0d47a1">
                        Build Genuine Member Connections
                      </Typography>
                      <Typography variant="body1" paragraph color="text.secondary">
                        Organizations using our Member Relationship Suite report:
                      </Typography>
                      
                      <List>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircleOutline sx={{ color: '#2196f3' }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary="54% increase in member satisfaction scores" 
                            secondary="Through personalized communication and better listening"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircleOutline sx={{ color: '#2196f3' }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary="41% higher survey response rates" 
                            secondary="Enabling better data-driven decisions about member needs"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircleOutline sx={{ color: '#2196f3' }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary="3.2x more peer-to-peer interactions" 
                            secondary="Creating a self-sustaining community that provides ongoing value"
                          />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '100%'
                        }}
                      >
                        <Box
                          component="img"
                          src="/relationship-suite-preview.png"
                          alt="Member Relationship Suite"
                          sx={{
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: 3,
                            boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div style="height: 250px; width: 100%; display: flex; justify-content: center; align-items: center; background: linear-gradient(135deg, #bbdefb 0%, #90caf9 100%); border-radius: 16px; color: white; font-weight: bold; font-size: 18px;">Relationship Suite Preview</div>';
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Box>
          </Container>
        </Box>
        
        {/* Member Request Suite Section */}
        <Box 
          ref={(el) => (sectionsRef.current[3] = el)}
          sx={{ 
            py: 10, 
            backgroundColor: 'white'
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ mb: 6, textAlign: 'center' }}>
              <Typography
                variant="subtitle1"
                color="primary"
                fontWeight="bold"
                gutterBottom
                sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
              >
                Membership Suite #3
              </Typography>
              <Typography
                variant="h3"
                component="h2"
                fontWeight="bold"
                gutterBottom
                sx={{
                  background: 'linear-gradient(90deg, #3f51b5 30%, #5c6bc0 70%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Member Request Suite
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                Streamline how your organization handles member requests, applications, and document submissions with customizable workflows
              </Typography>
            </Box>
            
            <Grid container spacing={4}>
              {requestFeatures.map((feature) => (
                <Grid item xs={12} md={6} key={feature.id}>
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                  >
                    <Paper
                      elevation={2}
                      sx={{
                        borderRadius: 4,
                        overflow: 'hidden',
                        height: '100%',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                        }
                      }}
                    >
                      <Box sx={{ p: 4 }}>
                        <Box
                          sx={{
                            display: 'inline-block',
                            borderRadius: '16px',
                            p: 1.5,
                            mb: 2,
                            background: feature.gradient
                          }}
                        >
                          {feature.icon}
                        </Box>
                        <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                          {feature.name}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                          {feature.description}
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
        
        {/* Admin Benefits Section */}
        <Box 
          ref={(el) => (sectionsRef.current[4] = el)}
          sx={{ 
            py: 10, 
            backgroundColor: '#f0f7ff'
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ mb: 6, textAlign: 'center' }}>
              <Typography
                variant="subtitle1"
                color="primary"
                fontWeight="bold"
                gutterBottom
                sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
              >
                For Administrators
              </Typography>
              <Typography
                variant="h3"
                component="h2"
                fontWeight="bold"
                gutterBottom
                sx={{
                  background: 'linear-gradient(90deg, #1565c0 30%, #0288d1 70%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Admin Benefits
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                Powerful tools to simplify administration, enhance financial oversight, and drive data-driven decisions
              </Typography>
            </Box>
            
            <Grid container spacing={4}>
              <Grid item xs={12} lg={8}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box
                      component={motion.div}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                      viewport={{ once: true }}
                    >
                      <Paper
                        elevation={2}
                        sx={{
                          borderRadius: 4,
                          overflow: 'hidden',
                          height: '100%',
                          transition: 'transform 0.3s, box-shadow 0.3s',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                          }
                        }}
                      >
                        <Box sx={{ p: 3 }}>
                          <Box
                            sx={{
                              display: 'inline-block',
                              borderRadius: '16px',
                              p: 1.5,
                              mb: 2,
                              background: 'linear-gradient(135deg, #bbdefb 0%, #64b5f6 100%)'
                            }}
                          >
                            <Payment sx={{ fontSize: 40, color: '#1565c0' }} />
                          </Box>
                          <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                            Expense Management
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph>
                          Track expenses with custom categories, receipt uploads, and easy approval workflows.
                          </Typography>
                          <List dense disablePadding>
                            <ListItem disableGutters>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CheckCircleOutline sx={{ color: '#1565c0', fontSize: 20 }} />
                              </ListItemIcon>
                              <ListItemText primary="Automated expense categorization" />
                            </ListItem>
                            <ListItem disableGutters>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CheckCircleOutline sx={{ color: '#1565c0', fontSize: 20 }} />
                              </ListItemIcon>
                              <ListItemText primary="Digital receipt storage" />
                            </ListItem>
                          </List>
                        </Box>
                      </Paper>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box
                      component={motion.div}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.1 }}
                      viewport={{ once: true }}
                    >
                      <Paper
                        elevation={2}
                        sx={{
                          borderRadius: 4,
                          overflow: 'hidden',
                          height: '100%',
                          transition: 'transform 0.3s, box-shadow 0.3s',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                          }
                        }}
                      >
                        <Box sx={{ p: 3 }}>
                          <Box
                            sx={{
                              display: 'inline-block',
                              borderRadius: '16px',
                              p: 1.5,
                              mb: 2,
                              background: 'linear-gradient(135deg, #c8e6c9 0%, #81c784 100%)'
                            }}
                          >
                            <TrendingUp sx={{ fontSize: 40, color: '#2e7d32' }} />
                          </Box>
                          <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                            Revenue Management
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph>
                          Track revenue with dashboards, forecasts, and detailed breakdowns by source.
                          </Typography>
                          <List dense disablePadding>
                            <ListItem disableGutters>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CheckCircleOutline sx={{ color: '#2e7d32', fontSize: 20 }} />
                              </ListItemIcon>
                              <ListItemText primary="Real-time revenue tracking" />
                            </ListItem>
                            <ListItem disableGutters>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CheckCircleOutline sx={{ color: '#2e7d32', fontSize: 20 }} />
                              </ListItemIcon>
                              <ListItemText primary="Multi-source forecasting" />
                            </ListItem>
                          </List>
                        </Box>
                      </Paper>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box
                      component={motion.div}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      viewport={{ once: true }}
                    >
                      <Paper
                        elevation={2}
                        sx={{
                          borderRadius: 4,
                          overflow: 'hidden',
                          height: '100%',
                          transition: 'transform 0.3s, box-shadow 0.3s',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                          }
                        }}
                      >
                        <Box sx={{ p: 3 }}>
                          <Box
                            sx={{
                              display: 'inline-block',
                              borderRadius: '16px',
                              p: 1.5,
                              mb: 2,
                              background: 'linear-gradient(135deg, #ffecb3 0%, #ffd54f 100%)'
                            }}
                          >
                            <CreditCard sx={{ fontSize: 40, color: '#f57f17' }} />
                          </Box>
                          <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                            Debt Management
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph>
                          Track overdue payments with reminders, payment plans, and flexible collection options.
                          </Typography>
                          <List dense disablePadding>
                            <ListItem disableGutters>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CheckCircleOutline sx={{ color: '#f57f17', fontSize: 20 }} />
                              </ListItemIcon>
                              <ListItemText primary="Automated payment reminders" />
                            </ListItem>
                            <ListItem disableGutters>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CheckCircleOutline sx={{ color: '#f57f17', fontSize: 20 }} />
                              </ListItemIcon>
                              <ListItemText primary="Flexible payment plans" />
                            </ListItem>
                          </List>
                        </Box>
                      </Paper>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box
                      component={motion.div}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.3 }}
                      viewport={{ once: true }}
                    >
                      <Paper
                        elevation={2}
                        sx={{
                          borderRadius: 4,
                          overflow: 'hidden',
                          height: '100%',
                          transition: 'transform 0.3s, box-shadow 0.3s',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                          }
                        }}
                      >
                        <Box sx={{ p: 3 }}>
                          <Box
                            sx={{
                              display: 'inline-block',
                              borderRadius: '16px',
                              p: 1.5,
                              mb: 2,
                              background: 'linear-gradient(135deg, #d1c4e9 0%, #b39ddb 100%)'
                            }}
                          >
                            <Assessment sx={{ fontSize: 40, color: '#4527a0' }} />
                          </Box>
                          <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                            Financial Reporting
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph>
                          Create financial reports with custom templates, exportable for stakeholder use. 
                          </Typography>
                          <List dense disablePadding>
                            <ListItem disableGutters>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CheckCircleOutline sx={{ color: '#4527a0', fontSize: 20 }} />
                              </ListItemIcon>
                              <ListItemText primary="Custom report builder" />
                            </ListItem>
                            <ListItem disableGutters>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CheckCircleOutline sx={{ color: '#4527a0', fontSize: 20 }} />
                              </ListItemIcon>
                              <ListItemText primary="Multiple export formats" />
                            </ListItem>
                          </List>
                        </Box>
                      </Paper>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box
                          component={motion.div}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: 0.4 }}
                          viewport={{ once: true }}
                        >
                          <Paper
                            elevation={2}
                            sx={{
                              borderRadius: 4,
                              overflow: 'hidden',
                              height: '100%',
                              transition: 'transform 0.3s, box-shadow 0.3s',
                              '&:hover': {
                                transform: 'translateY(-8px)',
                                boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                              }
                            }}
                          >
                            <Box sx={{ p: 3 }}>
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  borderRadius: '16px',
                                  p: 1.5,
                                  mb: 2,
                                  background: 'linear-gradient(135deg, #b2ebf2 0%, #4dd0e1 100%)'
                                }}
                              >
                                <Insights sx={{ fontSize: 40, color: '#0097a7' }} />
                              </Box>
                              <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                                Members Analytics
                              </Typography>
                              <Typography variant="body2" color="text.secondary" paragraph>
                                Leverage AI-powered insights to predict member behavior and identify at-risk members.
                              </Typography>
                              <List dense disablePadding>
                                <ListItem disableGutters>
                                  <ListItemIcon sx={{ minWidth: 36 }}>
                                    <CheckCircleOutline sx={{ color: '#0097a7', fontSize: 20 }} />
                                  </ListItemIcon>
                                  <ListItemText primary="Predictive churn analysis" />
                                </ListItem>
                                <ListItem disableGutters>
                                  <ListItemIcon sx={{ minWidth: 36 }}>
                                    <CheckCircleOutline sx={{ color: '#0097a7', fontSize: 20 }} />
                                  </ListItemIcon>
                                  <ListItemText primary="Personalized engagement suggestions" />
                                </ListItem>
                              </List>
                            </Box>
                          </Paper>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Box
                          component={motion.div}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: 0.5 }}
                          viewport={{ once: true }}
                        >
                          <Paper
                            elevation={2}
                            sx={{
                              borderRadius: 4,
                              overflow: 'hidden',
                              height: '100%',
                              transition: 'transform 0.3s, box-shadow 0.3s',
                              '&:hover': {
                                transform: 'translateY(-8px)',
                                boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                              }
                            }}
                          >
                            <Box sx={{ p: 3 }}>
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  borderRadius: '16px',
                                  p: 1.5,
                                  mb: 2,
                                  background: 'linear-gradient(135deg, #ffccbc 0%, #ff8a65 100%)'
                                }}
                              >
                                <Group sx={{ fontSize: 40, color: '#e64a19' }} />
                              </Box>
                              <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                                Bulk Member Tools
                              </Typography>
                              <Typography variant="body2" color="text.secondary" paragraph>
                                Efficiently manage large member groups with bulk import/export capabilities.
                              </Typography>
                              <List dense disablePadding>
                                <ListItem disableGutters>
                                  <ListItemIcon sx={{ minWidth: 36 }}>
                                    <CheckCircleOutline sx={{ color: '#e64a19', fontSize: 20 }} />
                                  </ListItemIcon>
                                  <ListItemText primary="CSV/Excel import & export" />
                                </ListItem>
                                <ListItem disableGutters>
                                  <ListItemIcon sx={{ minWidth: 36 }}>
                                    <CheckCircleOutline sx={{ color: '#e64a19', fontSize: 20 }} />
                                  </ListItemIcon>
                                  <ListItemText primary="Batch update capabilities" />
                                </ListItem>
                              </List>
                            </Box>
                          </Paper>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              
              <Grid item xs={12} lg={4}>
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <Paper 
                    elevation={3}
                    sx={{ 
                      borderRadius: 4,
                      p: 3,
                      height: '100%',
                      background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold" gutterBottom color="#0d47a1">
                      Admin Time Allocation
                    </Typography>
                    <Typography variant="body2" paragraph color="text.secondary">
                      How administrators typically spend their time before implementing our platform
                    </Typography>
                    
                    <Box sx={{ height: 300, mb: 3 }}>
                      <Pie 
                        data={adminTimeAllocationData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'bottom',
                            },
                          },
                        }}
                      />
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Member Request Approval SLA
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Set and monitor service level agreements for member requests to ensure timely responses.
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Critical Requests SLA
                          </Typography>
                        </Box>
                        <Box sx={{ width: 120 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, mr: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={93} 
                                sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(0,0,0,0.08)' }}
                              />
                            </Box>
                            <Typography variant="caption" fontWeight="bold">
                              93%
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Standard Requests SLA
                          </Typography>
                        </Box>
                        <Box sx={{ width: 120 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, mr: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={97} 
                                sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(0,0,0,0.08)' }}
                              />
                            </Box>
                            <Typography variant="caption" fontWeight="bold">
                              97%
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Low Priority Requests SLA
                          </Typography>
                        </Box>
                        <Box sx={{ width: 120 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, mr: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={99} 
                                sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(0,0,0,0.08)' }}
                              />
                            </Box>
                            <Typography variant="caption" fontWeight="bold">
                              99%
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)' }}>
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          Industry average SLA compliance is only 76% - our platform helps you achieve 93%+ compliance rates across all request types.
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
        
        {/* ROI Calculator Section */}
        <Box 
          ref={(el) => (sectionsRef.current[5] = el)}
          sx={{ 
            py: 10, 
            backgroundColor: '#f8f9fa'
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ mb: 6, textAlign: 'center' }}>
              <Typography
                variant="subtitle1"
                color="primary"
                fontWeight="bold"
                gutterBottom
                sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
              >
                Calculate Your Value
              </Typography>
              <Typography
                variant="h3"
                component="h2"
                fontWeight="bold"
                gutterBottom
                sx={{
                  background: 'linear-gradient(90deg, #2e7d32 30%, #4caf50 70%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                ROI Calculator
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                Discover the potential impact our platform can have on your organization's efficiency and revenue
              </Typography>
            </Box>
            
            <Paper 
              elevation={3}
              sx={{ 
                borderRadius: 4, 
                p: 3,
                mb: 4,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <TwoColumnLayout>
                {/* Left side - Organization Details */}
                <Box sx={{ height: '100%' }}>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Your Organization Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Enter your current metrics to calculate potential savings and benefits
                  </Typography>
                  
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Number of Members: {roiValues.members}
                    </Typography>
                    <Slider
                      value={roiValues.members}
                      onChange={handleRoiChange('members')}
                      min={50}
                      max={2000}
                      step={50}
                      sx={{ mb: 3 }}
                    />
                    
                    <Typography variant="subtitle2" gutterBottom>
                      Average Annual Membership Fee: ${roiValues.fees}
                    </Typography>
                    <Slider
                      value={roiValues.fees}
                      onChange={handleRoiChange('fees')}
                      min={20}
                      max={500}
                      step={10}
                      sx={{ mb: 3 }}
                    />
                    
                    <Typography variant="subtitle2" gutterBottom>
                      Current Collection Rate: {roiValues.collectionRate}%
                    </Typography>
                    <Slider
                      value={roiValues.collectionRate}
                      onChange={handleRoiChange('collectionRate')}
                      min={50}
                      max={95}
                      step={5}
                      sx={{ mb: 3 }}
                    />
                    
                    <Typography variant="subtitle2" gutterBottom>
                      Weekly Admin Hours: {roiValues.adminHours}
                    </Typography>
                    <Slider
                      value={roiValues.adminHours}
                      onChange={handleRoiChange('adminHours')}
                      min={5}
                      max={40}
                      step={1}
                      sx={{ mb: 3 }}
                    />
                    
                    <Typography variant="subtitle2" gutterBottom>
                      Admin Hourly Rate: ${roiValues.adminHourlyRate}
                    </Typography>
                    <Slider
                      value={roiValues.adminHourlyRate}
                      onChange={handleRoiChange('adminHourlyRate')}
                      min={15}
                      max={50}
                      step={1}
                      sx={{ mb: 3 }}
                    />
                  </Box>
                </Box>
                
                {/* Right side - ROI Results */}
                <Box 
                  sx={{ 
                    height: '100%',
                    background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                    p: 3,
                    borderRadius: 4,
                  }}
                >
                  <Typography variant="h5" fontWeight="bold" gutterBottom color="#2e7d32">
                    Your Potential Return on Investment
                  </Typography>
                  
                  <Box sx={{ mt: 3 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Paper 
                          elevation={1}
                          sx={{ 
                            p: 2, 
                            borderRadius: 2,
                            textAlign: 'center',
                            height: '100%'
                          }}
                        >
                          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                            Annual Revenue Increase
                          </Typography>
                          <Typography variant="h4" color="primary" fontWeight="bold">
                            {formatCurrency(roiResults?.revenueIncrease || 0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            From improved collection rate
                          </Typography>
                        </Paper>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Paper 
                          elevation={1}
                          sx={{ 
                            p: 2, 
                            borderRadius: 2,
                            textAlign: 'center',
                            height: '100%'
                          }}
                        >
                          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                            Admin Time Savings
                          </Typography>
                          <Typography variant="h4" color="primary" fontWeight="bold">
                            {formatCurrency(roiResults?.adminSavings || 0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            From automated processes
                          </Typography>
                        </Paper>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Paper 
                          elevation={1}
                          sx={{ 
                            p: 2, 
                            borderRadius: 2,
                            textAlign: 'center',
                            height: '100%'
                          }}
                        >
                          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                            Retention Value
                          </Typography>
                          <Typography variant="h4" color="primary" fontWeight="bold">
                            {formatCurrency(roiResults?.retentionValue || 0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            From reduced member churn
                          </Typography>
                        </Paper>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Paper 
                          elevation={1}
                          sx={{ 
                            p: 2, 
                            borderRadius: 2,
                            textAlign: 'center',
                            height: '100%'
                          }}
                        >
                          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                            Total Annual Savings
                          </Typography>
                          <Typography variant="h4" color="#2e7d32" fontWeight="bold">
                            {formatCurrency(roiResults?.totalAnnualSavings || 0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Combined annual benefit
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                    
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 2 }}>
                      <Typography variant="h5" align="center" gutterBottom fontWeight="bold" color="#2e7d32">
                        5-Year Value: {formatCurrency(roiResults?.fiveYearSavings || 0)}
                      </Typography>
                      <Typography variant="body1" align="center">
                        Estimated ROI: <Box component="span" fontWeight="bold">{Math.round(roiResults?.roi || 0)}%</Box>
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </TwoColumnLayout>
            </Paper>
          </Container>
        </Box>
        
        {/* Competitor Comparison Section */}
        <Box 
          ref={(el) => (sectionsRef.current[6] = el)}
          sx={{ 
            py: 10, 
            backgroundColor: 'white'
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ mb: 6, textAlign: 'center' }}>
              <Typography
                variant="subtitle1"
                color="primary"
                fontWeight="bold"
                gutterBottom
                sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
              >
                Why Choose Us
              </Typography>
              <Typography
                variant="h3"
                component="h2"
                fontWeight="bold"
                gutterBottom
                sx={{
                  background: 'linear-gradient(90deg, #673ab7 30%, #9c27b0 70%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Competitive Comparison
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                See how our platform stacks up against other membership management solutions
              </Typography>
            </Box>
            
            <Paper 
              elevation={3}
              sx={{ 
                borderRadius: 4, 
                overflow: 'hidden'
              }}
            >
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Feature</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: '#3f51b5' }}>
                          Our Platform
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Competitor A</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Competitor B</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {competitorComparison.map((item, index) => (
                      <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: 'rgba(0, 0, 0, 0.02)' } }}>
                        <TableCell sx={{ fontWeight: 'medium' }}>{item.feature}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <StarRating rating={item.us.rating} />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {item.us.note}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <StarRating rating={item.competitor1.rating} />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {item.competitor1.note}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <StarRating rating={item.competitor2.rating} />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {item.competitor2.note}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
            
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              sx={{ mt: 6 }}
            >
              <Paper 
                elevation={2}
                sx={{ 
                  borderRadius: 4, 
                  p: 4,
                  background: 'linear-gradient(135deg, #673ab7 0%, #9c27b0 100%)',
                  color: 'white'
                }}
              >
                <Grid container spacing={4} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                      Ready to transform your membership management?
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
                      Join thousands of organizations that have revolutionized how they engage with members
                    </Typography>
                    
                    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Chip 
                        label="99.9% Uptime SLA" 
                        sx={{ 
                          bgcolor: 'rgba(255,255,255,0.15)', 
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } 
                        }} 
                      />
                      <Chip 
                        label="24/7 Premium Support" 
                        sx={{ 
                          bgcolor: 'rgba(255,255,255,0.15)', 
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } 
                        }} 
                      />
                      <Chip 
                        label="Data Migration Assistance" 
                        sx={{ 
                          bgcolor: 'rgba(255,255,255,0.15)', 
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } 
                        }} 
                      />
                      <Chip 
                        label="Dedicated Success Manager" 
                        sx={{ 
                          bgcolor: 'rgba(255,255,255,0.15)', 
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } 
                        }} 
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Button
                        variant="contained"
                        component={Link}
                        to="/register-tenant"
                        endIcon={<ArrowForward />}
                        sx={{
                          bgcolor: 'white',
                          color: '#673ab7',
                          fontWeight: 'bold',
                          py: 1.5,
                          px: 4,
                          borderRadius: 2,
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.9)',
                          },
                        }}
                      >
                        Get Started Free
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          </Container>
        </Box>
      </div>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleCloseSnackbar}
            >
              <Close fontSize="small" />
            </IconButton>
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MarketingMaterialsPage;